/* eslint-disable max-len */
import React, {
  createContext, useContext, useRef, useEffect, useState, useCallback
} from 'react';
import proptypes from 'prop-types';
const { element, func, string, oneOfType, array, object } = proptypes;
import { ImpressionProvider } from '@thd-olt-component-react/impression';
import { useDataModel, useLazyDataModel } from '@thd-nucleus/data-sources';
import { useStore, ExperienceContext } from '@thd-nucleus/experience-context';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import { fbtRecsDataModel } from '../dataModel';
import {
  isRecsLoading,
  publish,
  titleBuilder,
  impressionContainerNameParser,
  getQuery,
  getOpts,
  getProductsCnt,
  getproductsData,
  getLoyaltyMembershipInput,
  getRecType,
  isInvalidRecsData,
  getProductTaxonomy,
  getCType,
  getAppId,
  getCurrentDate
} from '../helpers/helpers';

export const FBTContext = createContext({});
export const useFBTContext = () => useContext(FBTContext);

export const FBTProvider = ({
  itemId,
  onChange,
  apiName,
  onLoadDynamicRecs,
  children,
  tntData,
  queryProps,
  fusionContainerSlotName
}) => {
  const { storeId, storeZip: zipCode, membershipInformation } = useStore();
  const { customer, channel, isConsumerApp } = useContext(ExperienceContext);
  const { mcvisID, svocID } = useThdCustomer() || {};
  const [timeStamp] = useState(getCurrentDate());

  const { data: productData } = useDataModel('clientOnlyProduct', {
    variables: {
      itemId,
      storeId,
    },
    skip: !itemId,
    ssr: false
  });

  const taxonomyData = getProductTaxonomy(productData);
  const cType = getCType(customer?.type);

  const recsopts = {
    variables: {
      anchorId: itemId,
      storeId,
      isAnchorFirstResult: true,
      zipCode: zipCode || null,
      serviceType: null,
      apiName,
      loyaltyMembershipInput: getLoyaltyMembershipInput(membershipInformation),
      skipInstallServices: true,
      dataSource: apiName,
      recsContext: taxonomyData,
      cType,
      appId: getAppId(channel, isConsumerApp),
      mcvisId: mcvisID,
      svocId: svocID,
      timeStamp
    },
    ssr: false
  };

  const [renderData, setRenderData] = useState(null);
  const isDtsRef = useRef(null);
  const recsSchema = useRef(tntData?.schemaName ? tntData?.schemaName : apiName);
  const opts = getOpts(tntData, queryProps, recsopts);
  const query = getQuery(queryProps);

  const {
    data, loading, error
  } = useDataModel(query, opts);

  const [
    getInitialRecsContainer,
    {
      data: fallBackRecsData,
      loading: fallBackRecsLoading,
      error: fallBackRecsError
    }
  ] = useLazyDataModel('recs', recsopts);

  const productsCnt = getProductsCnt(tntData, data);
  const productsData = getproductsData(tntData, data);

  const shouldRequestFallback = () => {
    return (tntData?.isTNT && storeId !== '8119' && !loading && (error || !productsCnt > 0));
  };

  const [checkedItems, setCheckedItems] = useState([itemId] || []);

  const triggerTntAnalytics = (fallBackTrue) => {
    publish({
      componentName: 'recs-tnt',
      eventName: 'fallback-analytics',
      data: {
        testApiValue: recsSchema.current,
        fallbackValue: fallBackTrue || 'f',
        errorValue: (typeof error === 'undefined') ? 'n' : 'y',
        productsCntValue: productsCnt || 0
      }
    });
  };
  useEffect(() => {
    if (!loading && productsCnt > 0) {
      if (tntData?.isTNT) {
        triggerTntAnalytics();
      }
      setRenderData(productsData);
      recsSchema.current = tntData?.schemaName ? tntData?.schemaName : apiName;
      isDtsRef.current = query === 'dts';
    } else if (!loading && shouldRequestFallback()) {
      getInitialRecsContainer();
    }
  }, [data, loading]);

  useEffect(() => {
    if (!fallBackRecsLoading && !fallBackRecsError && fallBackRecsData?.recs?.products?.length > 0) {
      triggerTntAnalytics('t');
      setRenderData(fallBackRecsData?.recs);
      isDtsRef.current = false;
      recsSchema.current = apiName;
    }
  }, [fallBackRecsData, fallBackRecsLoading]);

  const handleDynamicRecsLoad = useCallback((resp) => {
    const products = isDtsRef.current ? resp?.dts?.products : resp?.recs?.products;
    if (products?.length) {
      const additionalIds = (products.map(({ product }) => product?.identifiers?.itemId).filter((val) => val));
      setCheckedItems([...additionalIds]);
    }
    if (onLoadDynamicRecs) {
      onLoadDynamicRecs(resp);
    }
  }, [itemId, onLoadDynamicRecs]);

  useEffect(() => {
    if (onChange && !loading && !fallBackRecsLoading && fallBackRecsData?.recs?.products?.length > 0) {
      handleDynamicRecsLoad(fallBackRecsData);
    } else {
      handleDynamicRecsLoad(data);
    }
  }, [itemId, data, onChange, fallBackRecsData]);

  if (isInvalidRecsData(tntData, loading, error, data)) {
    return null;
  }

  if (fallBackRecsError && !fallBackRecsLoading) {
    return null;
  }
  const recsTitle = renderData?.metadata?.title;
  const dynamicTitle = titleBuilder(tntData?.isTNT, customer?.type, tntData?.tntTitleCustomerType, tntData?.tntTitle, recsTitle, fallBackRecsData);

  const contextValue = {
    RecsFbtResponseData: renderData,
    RecsFbtResponseDataLoading: isRecsLoading(renderData, data, fallBackRecsData, loading, fallBackRecsLoading, tntData?.isTNT),
    checkedItems,
    setCheckedItems,
    apiName: recsSchema.current,
    dynamicTitle
  };

  return (
    <ImpressionProvider
      data={{
        id: '',
        component: 'ThdRecsFbt',
        name: impressionContainerNameParser(dynamicTitle),
        type: 'product',
        recType: getRecType(apiName),
        position: '',
        category: '',
        categoryPosition: '',
        ...(fusionContainerSlotName ? { sectionSlotID: fusionContainerSlotName } : {})
      }}
    >
      <FBTContext.Provider value={contextValue}>
        {children}
      </FBTContext.Provider>
    </ImpressionProvider>
  );
};

FBTProvider.propTypes = {
  itemId: string.isRequired,
  apiName: string.isRequired,
  onChange: func,
  onLoadDynamicRecs: func,
  children: element.isRequired,
  tntData: oneOfType([array, object]),
  queryProps: oneOfType([array, object]),
  fusionContainerSlotName: string
};

FBTProvider.dataModel = fbtRecsDataModel;
FBTProvider.defaultProps = {
  onChange: () => {},
  onLoadDynamicRecs: () => {},
  tntData: null,
  queryProps: null,
  fusionContainerSlotName: ''
};
